import React, { useState, useEffect } from 'react';
import Paper from "@material-ui/core/Paper";
import TablePagination from "@mui/material/TablePagination";
import Table from "react-bootstrap/Table";
import { Box } from "@material-ui/core";
import Grid from "@mui/system/Unstable_Grid/Grid";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import { useToast } from '../Context/ToastContext';
import { API_URL } from '../utils/constants';
import jwtInterceptor from '../service/jwtInterceptor';
import { Helmet } from 'react-helmet';
import TransactionFilterCard from '../component/TransactionFilterCard';

const fontWeightTitle = { fontWeight: "600" };

const tableStyles = {
    th: {
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
        backgroundColor: "#fff",
        fontWeight: 500,
        fontSize: "0.875rem",
        color: "rgba(0, 0, 0, 0.87)",
        padding: "17px"
    },
    td: {
        fontSize: "0.875rem",
        color: "rgba(0, 0, 0, 0.87)",
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
        padding: "17px"
    },
    tr: { '&:hover': { backgroundColor: '#f5f5f5' } },
    expandIcon: {
        cursor: 'pointer',
        color: '#1976d2',
        marginRight: '8px',
        verticalAlign: 'middle'
    }
};

const TransactionContainer = () => {
    const DEFAULT_PER_PAGE = 10;
    const DEFAULT_PAGE = 0;
    const toaster = useToast();

    const [total, setTotal] = useState(0);
    const [dataTable, setDataTable] = useState([]);
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PER_PAGE);
    const [loading, setLoading] = useState(true);
    const [expandedRows, setExpandedRows] = useState({});

    const [filters, setFilters] = useState({
        numero_requete: "", term: "", municipality: "", canton: "",
        commune_lieu: "", requerant_proprietaire: "", acquereur: "",
        dateRange: { startDate: null, endDate: null },
        priceRange: { minPrice: "", maxPrice: "" },
    });

    const columns = [
        { accessor: "numero_requete", show: "N° Transaction" },
        { accessor: "commune_lieu", show: "Adresse" },
        { accessor: "commune", show: "Commune" },
        { accessor: "requerant_proprietaire", show: "Vendeurs" },
        { accessor: "acquereur", show: "Acheteurs" },
        { accessor: "prix_vente", show: "Prix" },
    ];

    const toggleRowExpansion = (rowId) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowId]: !prev[rowId]
        }));
    };

    const renderCellContent = (text, isExpanded, truncateLen = 30) => {
        if (!text) return "-";
        const items = text.split(';').map(i => i.trim()).filter(i => i !== "");

        if (isExpanded) {
            return (
                <Box>
                    {items.map((item, i) => (
                        <div key={i} style={{ marginBottom: items.length > 1 ? '4px' : '0' }}>
                            {item}
                        </div>
                    ))}
                </Box>
            );
        }

        const firstItem = items[0] || "-";
        const displayedText = firstItem.length > truncateLen
            ? firstItem.substring(0, truncateLen) + "..."
            : firstItem;

        return (
            <span>
                {displayedText}
                {items.length > 1 && <span style={{ color: "#299CDB", fontWeight: 'bold' }}> (+{items.length - 1})</span>}
            </span>
        );
    };

    const getData = async (perPage, currentPage) => {
        try {
            const response = await jwtInterceptor.post(`${API_URL}/transaction/search`, {
                ...filters, size: perPage, page: currentPage
            });
            setTotal(response.data.data.total);
            setDataTable(response.data.data.results);
        } catch (error) {
            toaster.notifyError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setLoading(true);
        setPage(newPage);
        setExpandedRows({});
        getData(rowsPerPage, newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setLoading(true);
        const newRows = parseInt(event.target.value);
        setRowsPerPage(newRows);
        setExpandedRows({});
        getData(newRows, page);
    };

    const handleSearch = (filterParam) => {
        setFilters({ ...filterParam, isEmpty: false });
        searchfn({ ...filterParam, isEmpty: false });
    };

    const searchfn = async (filter) => {
        setLoading(true);
        const currentPage = filter.page !== undefined ? filter.page : 0;
        setFilters(filter);
        setPage(currentPage);
        setExpandedRows({});

        try {
            const response = await jwtInterceptor.post(`${API_URL}/transaction/search`, {
                ...filter, size: rowsPerPage, page: currentPage
            });
            setTotal(response.data.data.total);
            setDataTable(response.data.data.results);
        } catch (error) {
            toaster.notifyError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { getData(rowsPerPage, page); }, []);

    return (
        <Box className="my-4 w-100 px-4">
            <Helmet><title>Transactions - Mixdata</title></Helmet>
            <Grid container spacing={2}>
                <Grid className="filters shadow-none" xl={3} lg={3} md={4} sm={12} xs={12}>
                    <Paper className="shadow-none">
                        <TransactionFilterCard handleSearch={handleSearch} initialFilters={filters} />
                    </Paper>
                </Grid>
                <Grid xl={9} lg={9} md={8} sm={12} xs={12}>
                    <Box className="mb-3">
                        <h3 style={{ ...fontWeightTitle }}>Transactions</h3>
                    </Box>
                    <Box className="listtransactions h-auto w-100 m-0 position-relative z-index">
                        <Paper className="py-4 px-4 rounded shadow-none App-container m-0 overflow-hidden position-relative z-index">
                            <Box className="overflow-auto">
                                <Table>
                                    <thead style={{ background: "#eeeeee", borderBottom: "2px #DEE2E6 solid", color: "#363636" }}>
                                    <tr>
                                        {columns.map((field) => (
                                            <th key={field.accessor} style={tableStyles.th}>{field.show}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loading ? (
                                        <tr><td colSpan={columns.length} align="center" style={tableStyles.td}>Chargement...</td></tr>
                                    ) : dataTable.length > 0 ? (
                                        dataTable.map((transaction) => {
                                            const isExpanded = expandedRows[transaction._id] || false;
                                            const source = transaction._source;
                                            return (
                                                <tr key={transaction._id} style={tableStyles.tr}>
                                                    <td style={tableStyles.td}>
                                                            <span
                                                                onClick={() => toggleRowExpansion(transaction._id)}
                                                                style={tableStyles.expandIcon}
                                                            >
                                                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                            </span>
                                                        {source.numero_requete}
                                                    </td>
                                                    <td style={tableStyles.td}>{renderCellContent(source.commune_lieu, isExpanded, 25)}</td>
                                                    <td style={tableStyles.td}>{source.commune}</td>
                                                    <td style={tableStyles.td}>{renderCellContent(source.requerant_proprietaire, isExpanded, 25)}</td>
                                                    <td style={tableStyles.td}>{renderCellContent(source.acquereur, isExpanded, 25)}</td>
                                                    <td style={tableStyles.td}>
                                                        {source.prix_vente ? `${Number(source.prix_vente).toLocaleString('fr-CH')} CHF` : '—'}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr><td colSpan={columns.length} align="center" style={tableStyles.td}>Aucune transaction trouvée</td></tr>
                                    )}
                                    </tbody>
                                </Table>
                            </Box>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50]}
                                labelRowsPerPage="Lignes par page"
                            />
                        </Paper>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TransactionContainer;